import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Copy, Loader2, Mail, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useSenderDomainStore } from '@/features/email/stores/sender-domain.store';
import {
  normalizeSenderEmail,
  normalizeSenderName,
  validateSenderDomainForm,
} from '@/features/email/utils/sender-domain.util';

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
  toast.success('Copied to clipboard');
};

const StatusBadge = ({ status }: { status: 'pending' | 'verified' | 'failed' }) => {
  const config = {
    verified: {
      label: 'Verified',
      className: 'bg-green-100 text-green-800',
      icon: CheckCircle,
    },
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-800',
      icon: AlertCircle,
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-100 text-red-800',
      icon: AlertCircle,
    },
  }[status];

  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${config.className}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
};

export const SenderDomainSettings = () => {
  const {
    senderDomain,
    featureConfigured,
    isLoading,
    isSaving,
    isVerifying,
    isRemoving,
    error,
    fetchSenderDomain,
    saveSenderDomain,
    verifySenderDomain,
    removeSenderDomain,
    clearError,
  } = useSenderDomainStore();

  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');

  useEffect(() => {
    fetchSenderDomain().catch(() => undefined);
  }, [fetchSenderDomain]);

  useEffect(() => {
    if (!senderDomain) {
      return;
    }

    setFromEmail(senderDomain.fromEmail);
    setFromName(senderDomain.fromName || '');
  }, [senderDomain]);

  const isDirty = useMemo(() => {
    if (!senderDomain) {
      return fromEmail.trim().length > 0 || fromName.trim().length > 0;
    }

    return (
      normalizeSenderEmail(fromEmail) !== senderDomain.fromEmail ||
      normalizeSenderName(fromName) !== (senderDomain.fromName || '')
    );
  }, [fromEmail, fromName, senderDomain]);

  const handleSave = async () => {
    clearError();

    const normalizedPayload = {
      fromEmail: normalizeSenderEmail(fromEmail),
      fromName: normalizeSenderName(fromName) || null,
    };

    const validationError = validateSenderDomainForm(normalizedPayload);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const result = await saveSenderDomain(normalizedPayload);
    if (result.success) {
      toast.success(result.message || 'Sender domain saved');
    } else {
      toast.error(result.message || 'Failed to save sender domain');
    }
  };

  const handleVerify = async () => {
    const result = await verifySenderDomain();
    if (result.success) {
      toast.success(result.message || (result.verified ? 'Sender domain verified' : 'Verification is still pending'));
    } else {
      toast.error(result.message || 'Failed to verify sender domain');
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this sender domain and revert notifications to the default sender?')) {
      return;
    }

    const removed = await removeSenderDomain();
    if (removed) {
      setFromEmail('');
      setFromName('');
      toast.success('Sender domain removed');
    } else {
      toast.error(error || 'Failed to remove sender domain');
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
            <Mail className="h-5 w-5 text-blue-600" />
            Email Notification Domain
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-gray-600">
            Authenticate your company domain so applicant and broker notification emails can send
            from your address instead of the default AhiSign sender.
          </p>
        </div>
        {senderDomain ? <StatusBadge status={senderDomain.verificationStatus} /> : null}
      </div>

      {!featureConfigured ? (
        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          SendGrid is not configured in this environment yet. Once the backend has a SendGrid API
          key, this section will generate the DNS records your users need.
        </div>
      ) : null}

      {error ? (
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-gray-700">From email</span>
          <input
            value={fromEmail}
            onChange={(event) => setFromEmail(event.target.value)}
            placeholder="notifications@examplecompany.com"
            disabled={isLoading || !featureConfigured}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
          />
          <p className="mt-1 text-xs text-gray-500">
            We automatically authenticate the domain from this address.
          </p>
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-gray-700">From name</span>
          <input
            value={fromName}
            onChange={(event) => setFromName(event.target.value)}
            placeholder="Example Company"
            disabled={isLoading || !featureConfigured}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
          />
        </label>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={!featureConfigured || isLoading || isSaving || !isDirty}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {senderDomain ? 'Save changes' : 'Create sender domain'}
        </button>

        {senderDomain ? (
          <>
            <button
              type="button"
              onClick={handleVerify}
              disabled={isVerifying || isRemoving}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Verify domain
            </button>

            <button
              type="button"
              onClick={handleRemove}
              disabled={isRemoving || isSaving || isVerifying}
              className="inline-flex items-center gap-2 rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Remove domain
            </button>
          </>
        ) : null}
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        {senderDomain?.verificationStatus === 'verified' ? (
          <p>
            Verified. Notification emails now send from{' '}
            <span className="font-medium text-slate-900">{senderDomain.fromEmail}</span>.
          </p>
        ) : (
          <p>
            Until verification is complete, notification emails keep using the default AhiSign
            sender.
          </p>
        )}
      </div>

      {senderDomain?.dnsRecords?.length ? (
        <div className="mt-6 space-y-3">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              DNS Records
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Add these records at your DNS provider exactly as shown, then click Verify.
            </p>
          </div>

          <div className="space-y-2">
            {senderDomain.dnsRecords.map((record) => (
              <div
                key={`${record.purpose}-${record.name}`}
                className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-[160px_1fr_1fr]"
              >
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Type</p>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(record.type)}
                      className="inline-flex items-center justify-center rounded-md border border-slate-300 p-1.5 text-slate-700 transition-colors hover:bg-slate-100"
                      title="Copy type"
                      aria-label="Copy type"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mt-1 font-mono text-sm text-slate-900">{record.type}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Host</p>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(record.name)}
                      className="inline-flex items-center justify-center rounded-md border border-slate-300 p-1.5 text-slate-700 transition-colors hover:bg-slate-100"
                      title="Copy host"
                      aria-label="Copy host"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mt-1 break-all font-mono text-sm text-slate-900">{record.name}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Value
                    </p>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(record.value)}
                      className="inline-flex items-center justify-center rounded-md border border-slate-300 p-1.5 text-slate-700 transition-colors hover:bg-slate-100"
                      title="Copy value"
                      aria-label="Copy value"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mt-1 break-all font-mono text-sm text-slate-900">{record.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
